/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
} from "@mui/material";
import { ReactComponent as PreviousIcon } from "assets/images/icons/previous.svg";
import { ReactComponent as NextIcon } from "assets/images/icons/next.svg";
import Dropdown from "./dropdown";
import { columns } from "./columns.js";

const MobileGrid = (props) => {
    const [gridData, setGridData] = useState([]);
    
    // Use props for pagination instead of local state
    const currentPageNumber = props?.currentPage || 1;
    const itemsPerPage = props?.pageSize || 5;
    const totalItemCount = props?.pagination?.total || 0;

    // Use data directly from props (server-side pagination)
    useEffect(() => {
        setGridData(props?.data || []);
    }, [props?.data]);

    const handlePageChange = (event, newPageNumber) => {
        props?.onPageChange?.(event, newPageNumber);
    };

    const handleItemsPerPageChange = (event) => {
        props?.onPageSizeChange?.(event);
    };

    const totalPageCount = Math.ceil(totalItemCount / itemsPerPage);

    // Pagination handler functions
    const handlePreviousPageClick = () => {
        if (currentPageNumber > 1) {
            handlePageChange(null, currentPageNumber - 1);
        }
    };

    const handleNextPageClick = () => {
        if (currentPageNumber < totalPageCount) {
            handlePageChange(null, currentPageNumber + 1);
        }
    };

    const handlePageDropdownChange = (event) => {
        handlePageChange(null, parseInt(event?.target?.value, 10));
    };

    // Function to render evidence for mobile
    const renderEvidenceLinks = (evidenceArray) => {
        if (!evidenceArray || evidenceArray.length === 0) {
            return "None";
        }
        
        return evidenceArray.map((evidenceItem, itemIndex) => {
            return (
                <a 
                    key={`${evidenceItem?.filename}-${itemIndex}`}
                    href={evidenceItem?.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="data-mobile-grid-evidence-link"
                >
                    {evidenceItem?.title || evidenceItem?.filename}
                </a>
            );
        });
    };

    // Function to format date and time for mobile grid
    const formatDateTime = (dataRow, fieldType) => {
        if (fieldType === "date") {
            const dateValue = dataRow?.date;
            if (dateValue) {
                // If date is already in YYYY-MM-DD format, return as is
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                    return dateValue;
                }
                // Otherwise, try to parse and format
                const date = new Date(dateValue);
                return date.toLocaleDateString("en-CA"); // YYYY-MM-DD format
            }
            return "N/A";
        } else if (fieldType === "time") {
            const timeValue = dataRow?.time;
            if (timeValue) {
                // If time is already in HH:MM:SS format, convert to 12-hour format
                if (/^\d{2}:\d{2}:\d{2}$/.test(timeValue)) {
                    const [hours, minutes] = timeValue.split(':');
                    const hour12 = parseInt(hours) % 12 || 12;
                    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                    return `${hour12}:${minutes} ${ampm}`;
                }
                // Otherwise, try to parse as full datetime
                const date = new Date(timeValue);
                const timeString = date.toLocaleTimeString("en-CA", { 
                    hour: "2-digit", 
                    minute: "2-digit", 
                    hour12: true 
                });
                return timeString.replace(/a\.m\./gi, "AM").replace(/p\.m\./gi, "PM");
            }
            return "N/A";
        }
        return dataRow?.[fieldType] || "N/A";
    };


    if (gridData.length === 0) {
        return (
            <div className="data-mobile-grid-container">
                <div className="data-mobile-grid-no-data">
                    No data found
                </div>
            </div>
        );
    }

    return (
        <div className="data-mobile-grid-container">
            <div className="data-mobile-grid-main">
                {gridData?.map((dataRow, rowIndex) => (
                    <div 
                        key={dataRow?.id || rowIndex}
                        className={`data-mobile-grid-row ${rowIndex % 2 === 0 ? "data-mobile-grid-row-even" : "data-mobile-grid-row-odd"}`}
                    >
                        {columns?.map((columnDefinition) => (
                            <div key={columnDefinition?.id} className="data-mobile-grid-column">
                                <div className="data-mobile-grid-label">
                                    {columnDefinition?.label}:
                                </div>
                                <div className="data-mobile-grid-value">
                                    {columnDefinition?.id === "evidence" ? 
                                        renderEvidenceLinks(dataRow?.[columnDefinition?.id]) : 
                                        (columnDefinition?.id === "date" || columnDefinition?.id === "time") ?
                                            formatDateTime(dataRow, columnDefinition?.id) :
                                            dataRow?.[columnDefinition?.id] || "N/A"
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            
            {/* Pagination Controls */}
            <Box className="mobile-grid-pagination-container">
                <Box className="mobile-grid-pagination-controls">
                    <Box className="mobile-grid-pagination-button-group">
                        <div 
                            onClick={handlePreviousPageClick}
                            className={`data-mobile-grid-pagination-button ${currentPageNumber <= 1 ? "disabled" : ""}`}
                        >
                            <PreviousIcon />
                        </div>
                        <div 
                            onClick={handleNextPageClick}
                            className={`data-mobile-grid-pagination-button ${currentPageNumber >= totalPageCount ? "disabled" : ""}`}
                        >
                            <NextIcon />
                        </div>
                        <div className="data-mobile-grid-page-dropdown">
                            <Dropdown
                                value={currentPageNumber}
                                onChange={handlePageDropdownChange}
                                options={Array.from({ length: totalPageCount }, (_, pageIndex) => ({
                                    value: pageIndex + 1,
                                    label: `Page ${pageIndex + 1}`
                                }))}
                                fullWidth={false}
                                width="clamp(150px, 20vw, 200px)"
                            />
                        </div>
                    </Box>
                    <Box className="mobile-grid-pagination-items-group">
                        <Typography component="div" className="mobile-grid-pagination-label">
                            Items per page:
                        </Typography>
                        <Dropdown
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            options={[
                                { value: 5, label: "5" },
                                { value: 10, label: "10" },
                                { value: 25, label: "25" },
                                { value: 50, label: "50" }
                            ]}
                            fullWidth={false}
                            width="86px"
                        />
                    </Box>
                </Box>
            </Box>
        </div>
    );
};

MobileGrid.displayName = "MobileGrid";
export { MobileGrid };
